import { QueryClient } from '@tanstack/react-query';
import api from '@/services/api';

/**
 * Unified search result from the backend
 */
export interface SearchResult {
  type: 'product' | 'documentation';
  id: string;
  title: string;
  snippet: string;
  url: string;
  score: number;
  metadata: Record<string, any>;
}

/**
 * Search response from the backend
 */
export interface SearchResponse {
  query: string;
  total_results: number;
  products: SearchResult[];
  documentation: SearchResult[];
  product_count: number;
  documentation_count: number;
  search_time_ms: number;
}

/**
 * Search request parameters
 */
export interface SearchParams {
  q: string;
  limit?: number;
  types?: Array<'product' | 'documentation'>;
  language?: string;
}

/**
 * Search suggestions response
 */
export interface SearchSuggestionsResponse {
  suggestions: string[];
}

/**
 * Service for global search functionality
 */
export class SearchService {
  private static readonly SEARCH_ENDPOINT = '/api/search/';
  private static readonly SUGGESTIONS_ENDPOINT = '/api/search/suggestions/';

  /**
   * Perform a unified search across products and documentation
   */
  static async searchAll(params: SearchParams): Promise<SearchResponse> {
    try {
      const searchParams = new URLSearchParams();

      // Add required query parameter
      searchParams.append('q', params.q);

      // Add optional parameters
      if (params.limit) {
        searchParams.append('limit', params.limit.toString());
      }

      if (params.types && params.types.length > 0) {
        searchParams.append('types', params.types.join(','));
      }

      if (params.language) {
        searchParams.append('language', params.language);
      }

      const response = await api.get(`${this.SEARCH_ENDPOINT}?${searchParams.toString()}`);
      return response.data;
    } catch {
      throw new Error('Search request failed. Please try again.');
    }
  }

  /**
   * Get search suggestions for typeahead functionality
   */
  static async getSuggestions(
    query: string,
    limit: number = 5
  ): Promise<SearchSuggestionsResponse> {
    try {
      if (query.length < 2) {
        return { suggestions: [] };
      }

      const searchParams = new URLSearchParams();
      searchParams.append('q', query);
      searchParams.append('limit', limit.toString());

      const response = await api.get(`${this.SUGGESTIONS_ENDPOINT}?${searchParams.toString()}`);
      return response.data;
    } catch {
      // Return empty suggestions on error to not break the UI
      return { suggestions: [] };
    }
  }

  /**
   * Search only products
   */
  static async searchProducts(query: string, limit: number = 10): Promise<SearchResponse> {
    return this.searchAll({
      q: query,
      limit,
      types: ['product'],
    });
  }

  /**
   * Search only documentation
   */
  static async searchDocumentation(
    query: string,
    limit: number = 10,
    language: string = 'en'
  ): Promise<SearchResponse> {
    return this.searchAll({
      q: query,
      limit,
      types: ['documentation'],
      language,
    });
  }
}

/**
 * React Query keys for search operations
 */
export const searchQueryKeys = {
  all: ['search'] as const,
  search: (params: SearchParams) => ['search', 'unified', params] as const,
  suggestions: (query: string, limit?: number) => ['search', 'suggestions', query, limit] as const,
  products: (query: string, limit?: number) => ['search', 'products', query, limit] as const,
  documentation: (query: string, limit?: number, language?: string) =>
    ['search', 'documentation', query, limit, language] as const,
};

/**
 * Utility function to invalidate all search-related queries
 */
export const invalidateSearchQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: searchQueryKeys.all });
};
